import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { CheckCircle, Warning, ArrowRight, Receipt } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function BillingCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState({ phase: "checking", message: "Confirming your payment with Stripe…" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ phase: "error", message: "Missing session reference. Head back to pricing and try again." });
      return;
    }
    const token = localStorage.getItem("pg_token");
    if (!token) {
      navigate(`/login?next=/billing/callback?session_id=${sessionId}`);
      return;
    }
    let timer;
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const r = await axios.get(`${API}/billing/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ps = r.data?.payment_status;
        if (ps === "paid") {
          // refresh the auth context so the Pro badge shows up immediately
          try { await checkAuth(); } catch {}
          setState({ phase: "success", message: "Welcome to Pro. Your account has been upgraded.", planId: r.data?.plan_id });
          return;
        }
        if (r.data?.status === "expired") {
          setState({ phase: "error", message: "Checkout session expired. Please try again." });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "pending", message: "Stripe is still processing your payment. We'll email you the moment it's confirmed - refresh your dashboard in a minute." });
          return;
        }
        setState({ phase: "checking", message: "Confirming your payment with Stripe…" });
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ phase: "error", message: e?.response?.data?.detail || "Could not verify the payment. Contact support@priceguard.nz if charged." });
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [params, navigate]);

  return (
    <div className="min-h-screen bg-white" data-testid="billing-callback-page">
      <Header />
      <main className="max-w-[800px] mx-auto px-6 lg:px-10 py-20">
        {state.phase === "checking" && (
          <div className="border border-[#e4e4e7] bg-[#f7f7f8] p-10" data-testid="billing-callback-checking">
            <div className="pg-overline">Step 02</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">Hang tight - confirming with Stripe.</h1>
            <p className="text-[#52525b] mt-4">{state.message}</p>
            <div className="mt-6 h-1 w-full bg-[#e4e4e7] overflow-hidden">
              <div className="h-full bg-[#002FA7] animate-pulse" style={{ width: "60%" }} />
            </div>
          </div>
        )}
        {state.phase === "success" && (
          <div className="border border-[#16A34A] bg-white p-10" data-testid="billing-callback-success">
            <CheckCircle size={48} weight="fill" color="#16A34A" />
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-4">You're on Pro<span className="text-[#002FA7]">.</span></h1>
            <p className="text-[#52525b] mt-4 leading-relaxed">{state.message} Tracking caps are lifted, and Vault features will unlock for your account the moment they ship.</p>
            <Link to="/dashboard" className="mt-8 inline-flex items-center gap-2 bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A]" data-testid="billing-success-dashboard-cta">
              Open dashboard <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        )}
        {state.phase === "pending" && (
          <div className="border border-[#D97706] bg-white p-10" data-testid="billing-callback-pending">
            <Receipt size={40} weight="duotone" color="#D97706" />
            <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-4">Payment received - confirmation pending.</h1>
            <p className="text-[#52525b] mt-4 leading-relaxed">{state.message}</p>
            <Link to="/dashboard" className="mt-6 inline-flex items-center gap-2 border border-[#0a0a0a] font-bold px-6 py-3 hover:bg-[#0a0a0a] hover:text-white transition-colors">
              Back to dashboard
            </Link>
          </div>
        )}
        {state.phase === "error" && (
          <div className="border border-[#FF3B30] bg-white p-10" data-testid="billing-callback-error">
            <Warning size={40} weight="fill" color="#FF3B30" />
            <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-4">Something didn't add up.</h1>
            <p className="text-[#52525b] mt-4 leading-relaxed">{state.message}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing" className="bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A]">Try checkout again</Link>
              <a href="mailto:support@priceguard.nz" className="border border-[#0a0a0a] font-bold px-6 py-3 hover:bg-[#0a0a0a] hover:text-white transition-colors">Email support</a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
